import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import PropTypes from "prop-types";
import CheckDataForm from "./CheckDataForm";
import CompanyDataForm from "./CompanyDataForm";
import NGUMStatusObserver from "../../../NGum/containers/NGUMStatusObserver";

const defaultModel = {
  firstname: "",
  lastname: "",
  email: "",
  cellNumber: "",
  username: "",
  telephoneNumber: "",
  preferredContactMethod: "cell_number",
  companies: [
    {
      name: "",
      role: "",
      telephoneNumber: "",
      extendedRoles: [],
    },
  ],
};

const getInitialState = (props) => {
  const model = props.model || {};
  const checkData = {
    id: model.id,
    firstname: model.firstname || "",
    lastname: model.lastname || "",
    email: model.email || "",
    cellNumber: model.cellNumber || "",
    preferredContactMethod: model.preferredContactMethod || "cell_number",
  };
  let mainCompany = null;
  const companies = [];
  if (props.userChecked) {
    // creating new person
    if (props.userExists) {
      companies.push({ ...model.companies[0] });
    } else {
      mainCompany = { ...model.companies[0] };
    }
  } else {
    for (const company of model.companies) {
      console.log(
        ">> BasicDataForm: checking company",
        company,
        "userChecked",
        props.userChecked,
        "userExists",
        props.userExists,
      );
      if (mainCompany === null && company.role) {
        mainCompany = { ...company };
      } else {
        companies.push({ ...company });
      }
    }
    // imported from CSV without role
    if (mainCompany === null && companies.length > 0 && props.userExists) {
      mainCompany = { ...companies[0] };
    }
  }
  return { checkData, mainCompany, companies };
};

const BasicDataForm = forwardRef(function BasicDataForm(props, ref) {
  const {
    className = "",
    model = { ...defaultModel },
    kdlCompanyList = [],
    contractGivers = [],
    userCompany,
    roleList = [],
    extendedRoleList,
    checkUser,
    userChecked,
    userExists,
    canReadAdminData,
    disabled: isDisabled,
    readOnly = false,
  } = props;

  const disabled = React.useMemo(
    () => isDisabled || readOnly,
    [isDisabled, readOnly],
  );
  const [formState, setFormState] = useState(() => getInitialState(props));

  // Keep refs current every render so imperative handles never read stale values
  const formStateRef = useRef(formState);
  const propsRef = useRef(props);
  formStateRef.current = formState;
  propsRef.current = props;

  const checkDataRef = useRef(null);
  const mainCompanyRef = useRef(null);

  // Reset derived state when user-check status or existence changes
  useEffect(() => {
    setFormState(getInitialState(propsRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userChecked, userExists]);

  useImperativeHandle(
    ref,
    () => ({
      getModel: (noValidate) => {
        const model = checkDataRef.current
          ? checkDataRef.current.getModel(noValidate)
          : null;
        if (!model) return null;

        model.companies = [];
        if (mainCompanyRef.current) {
          const mainCompany = mainCompanyRef.current.getModel(noValidate);
          if (!mainCompany) return null;
          console.log(">>  model of main company", mainCompany);
          mainCompany.telephoneNumber = model.telephoneNumber;
          delete model.telephoneNumber;
          model.companies.push(mainCompany);
        } else {
          console.log(
            "<< MainCompany not set => first secondary company is required !",
          );
        }

        if (model.companies.length === 0) return null;
        if (propsRef.current.model && propsRef.current.model.id) {
          model.id = propsRef.current.model.id;
        }
        return model;
      },
      getCompanyData: () => {
        if (mainCompanyRef.current) {
          return mainCompanyRef.current.getCompanyData();
        }
        return { isContractor: false };
      },
      generateUsername: (newName, newSurname) => {
        // Note: translates original behaviour — fields were not in class state either
        setFormState((prev) => ({
          ...prev,
          firstname: newName || prev.firstname,
          lastname: newSurname || prev.lastname,
          username: `${newName || prev.firstname}.${newSurname || prev.lastname}`,
        }));
      },
    }),
    [],
  ); // empty deps — reads go through refs

  const { checkData, mainCompany } = formState;

  const mergeCompanyLists = (cmp1, cmp2) => {
    let list = [];
    if (cmp1) {
      list = [...cmp1];
    }
    (cmp2 || []).forEach((company) => {
      const found = list.find((item) => item.name === company.name);
      if (!found) list.push({ ...company });
    });
    return list;
  };
  const kdlCompanies = mergeCompanyLists(kdlCompanyList, contractGivers);
  // console.log("BasicDataForm()", model, checkData, "userChecked:", userChecked, "userExists:", userExists);

  const { username, password, newEmail, email_status_id } = model;
  let telephoneNumber = mainCompany ? mainCompany.telephoneNumber : "";
  if (
    telephoneNumber === "" &&
    model.telephoneNumber &&
    model.telephoneNumber !== ""
  ) {
    telephoneNumber = model.telephoneNumber;
  }

  return (
    <div className={className}>
      <NGUMStatusObserver>
        <CheckDataForm
          ref={checkDataRef}
          model={{
            ...checkData,
            username,
            password,
            newEmail,
            email_status_id,
            telephoneNumber,
          }}
          userChecked={userChecked}
          checkUser={checkUser}
          userExists={userExists}
          disabled={disabled}
        />
      </NGUMStatusObserver>
      {mainCompany && (
        <NGUMStatusObserver>
          <CompanyDataForm
            ref={mainCompanyRef}
            namePrefix={`mainCompany-`}
            className="pad-top"
            model={mainCompany}
            kdlCompanyList={kdlCompanies}
            userCompany={userCompany}
            roleList={roleList}
            extendedRoleList={extendedRoleList}
            disabled={disabled}
            userExists={userExists}
            canReadAdminData={canReadAdminData}
            isMain
          />
        </NGUMStatusObserver>
      )}
    </div>
  );
});

BasicDataForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.object,
  kdlCompanyList: PropTypes.array,
  contractGivers: PropTypes.array,
  userCompany: PropTypes.object,
  roleList: PropTypes.array,
  checkUser: PropTypes.func,
  extendedRoleList: PropTypes.array,
  userExists: PropTypes.bool,
  userChecked: PropTypes.bool,
  canReadAdminData: PropTypes.bool,
  disabled: PropTypes.bool,
  editingMyself: PropTypes.bool,
};

export default BasicDataForm;
